import styled, { css } from 'styled-components'

import Button from 'components/Button'
import BaseImage, { BaseImageComponent } from 'components/Image'

import { ReactComponent as EllipsisIcon } from 'icons/ellipsis-20.svg'
import { TableComponents } from 'components/Table'
import PersonAvatar from 'components/PersonAvatar'
import { truncateLines } from 'components/Truncate'

export const StatusIcon = styled.svg.attrs({
  role: 'img',
})`
  position: absolute;

  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  /* This color should be "Light/colors.foreground.secondary.4" but given the icon composition */
  /* Opacity needs to be applied to the whole object */
  color: ${({ theme }) => theme.colors.foreground.secondary[1]};
  opacity: 0.55;

  margin: auto;
`

export const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  padding: 16px;

  flex-shrink: 0;

  /* Make sure the Wrapper is a responsive rectangle  */
  :before {
    content: '';
    display: block;
    padding-bottom: 100%;
  }

  /* Create the border inside */
  :after {
    content: '';
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.border[1]};
    position: absolute;

    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`

export const Image = styled(BaseImage)`
  position: absolute;

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  display: flex;
  justify-content: center;
  align-items: center;
`

export const ImageElement = styled(BaseImageComponent).attrs({
  draggable: false,
})`
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;

  /* stylelint-disable font-family-no-missing-generic-family-keyword */
  font-family: 'object-fit: contain;';
`

export const Name = styled.h1`
  /* Fix the default margin of the element */
  margin: 0;

  color: ${({ theme }) => theme.colors.foreground.secondary[1]};

  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  white-space: break-spaces;
  word-break: break-word;
`

export const SubtitleTextWrapper = styled.span`
  display: inline-flex;
  align-content: center;
  gap: 8px;

  font-size: 0.75rem;
  line-height: 1.3;

  font-weight: 400;
  color: ${({ theme }) => theme.colors.foreground.secondary[4]};

  b {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.foreground.secondary[2]};
  }
`

/**
 * Dropdown Section
 */
export const DropdownButton = styled(Button).attrs({
  variant: 'secondary',
  tiny: true,
})`
  border-radius: ${({ theme }) => theme.radii.medium};
  background-clip: padding-box;
  min-height: 32px;
  padding: 4px 8px;
`

export const DropdownButtonEllipsis = styled(EllipsisIcon)`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.foreground.secondary[4]};
`

export const AvatarTooltipWrapper = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;

  ${PersonAvatar} {
    margin-right: 8px;
  }
`

export const DropdownButtonWrapper = styled.div`
  position: absolute;

  top: 9px;
  right: 9px;

  opacity: 0;
  transform: translate3d(0, -5px, 0);
  transition: ${({ theme }) => `
    opacity ${theme.transitions.duration[0]} ${theme.transitions.timing.easeInOut},
    transform ${theme.transitions.duration[0]} ${theme.transitions.timing.easeInOut};
  `};

  /* Prevent the button from hiding when the dropdown is open */
  [aria-expanded='true'] ${DropdownButton} {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }

  :focus-within {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`

export const GridFocusStyles = css`
  /* stylelint-disable-next-line no-descending-specificity */
  ${DropdownButtonWrapper} {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`

export const NameWrapper = styled.div`
  display: flex;
  align-items: center;
  white-space: pre-wrap;
`

/**
 * Wrapper Section
 */
export const commonWrapper = css`
  & .shrink {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & .keep-size {
    flex-shrink: 0;
  }

  & .wrap {
    overflow-wrap: break-word;
    word-wrap: break-word;
    hyphens: auto;
  }

  &[role='button'] {
    cursor: pointer;
  }
`

export const GridWrapper = styled.section`
  ${commonWrapper};

  display: flex;
  flex-direction: column;
  position: relative;

  && ${ImageWrapper} {
    border-radius: ${({ theme }) => theme.radii.large};
    background-color: ${({ theme }) => theme.colors.background.secondary[2]};

    /* Margin from image wrapper to the document name */
    margin-bottom: 6px; /* stylelint-disable-line scales/space */

    /* Maintain the radius in the inside border */
    ::after {
      border-radius: ${({ theme }) => theme.radii.large};
    }
  }

  ${StatusIcon} {
    width: 64px;
    height: 64px;
  }

  ${Name} {
    margin-bottom: 2px; /* stylelint-disable-line scales/space */
  }

  @media (hover: none) {
    ${GridFocusStyles}
  }

  &:hover,
  &:focus,
  &:focus-within {
    ${GridFocusStyles}
  }
`

export const TableWrapper = styled(TableComponents.TableRow)`
  ${commonWrapper};

  && ${ImageWrapper} {
    /* We need to make sure the size is set to prevent the image from expanding/shrinking */
    width: 32px;
    height: 32px;

    border-radius: ${({ theme }) => theme.radii.medium};
    background-color: ${({ theme }) => theme.colors.state.hover};

    /* Margin from image wrapper to the document name */
    margin-right: 12px;

    /* Maintain the radius in the inside border */
    ::after {
      border-radius: ${({ theme }) => theme.radii.medium};
    }
  }

  ${StatusIcon} {
    width: 16px;
    height: 16px;
  }
`

export const TableCell = styled(TableComponents.TableCell)`
  padding: 12px 0 12px 12px;
  flex: 1;

  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.foreground.secondary[4]};

  :last-child {
    flex-grow: 0;
    flex-shrink: 0;
    padding-right: 12px;
    min-width: 64px;
  }
`

export const DropdownWrapper = styled.span`
  line-height: 1;
`

export const TableAvatarNames = styled.span`
  word-break: break-word;
  ${truncateLines(3)};

  margin-left: 8px;
`

export const GridPlaceholderWrapper = styled(GridWrapper)`
  margin-bottom: 44px;

  ${/* sc-selector */ ImageWrapper}:after {
    box-shadow: none;
  }
`

export const TablePlaceholderWrapper = styled(TableWrapper)`
  ${/* sc-selector */ ImageWrapper}:after {
    box-shadow: none;
  }
`
